import { useEffect, useRef, useState } from "react";

const STORAGE_KEY = "rf_location";

const readSaved = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || null;
  } catch {
    return null;
  }
};
const writeSaved = (loc) => {
  try {
    if (loc) localStorage.setItem(STORAGE_KEY, JSON.stringify(loc));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
};

// Detected delivery location for the header's plain "deliver to" display.
// Reverse geocoding uses OpenStreetMap's free Nominatim endpoint (no API
// key). Accuracy is bounded by the browser's own geolocation fix — no
// geocoder can correct a coordinate that's already off — so results on a
// desktop without GPS can be approximate; a phone with GPS is accurate.
export default function useDeliveryLocation() {
  const [loc, setLoc] = useState(readSaved);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const triedAuto = useRef(false);

  const detect = () => {
    if (!navigator.geolocation) {
      setError("Your browser doesn't support location detection.");
      return;
    }
    setBusy(true);
    setError("");
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}&zoom=16`
          );
          const data = await res.json();
          const a = data.address || {};
          const label =
            a.suburb || a.neighbourhood || a.village || a.town || a.city_district || a.city || "Your location";
          const address = data.display_name || `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
          const next = { label, address };
          setLoc(next);
          writeSaved(next);
        } catch {
          setError("Couldn't look up your address right now.");
        } finally {
          setBusy(false);
        }
      },
      (err) => {
        setBusy(false);
        setError(
          err.code === err.PERMISSION_DENIED
            ? "Location access denied — allow it in your browser to detect your area."
            : "Couldn't detect your location right now."
        );
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 }
    );
  };

  // Auto-attempt once on first load if nothing's saved yet.
  useEffect(() => {
    if (!loc && !triedAuto.current) {
      triedAuto.current = true;
      detect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { loc, busy, error, detect };
}
